import React, { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, Mail, ArrowRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [status, setStatus] = useState("loading"); // loading, success, error, no-token
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("no-token");
      return;
    }

    const verifyEmail = async () => {
      try {
        const response = await axios.get(`${API}/auth/verify-email/${token}`);
        setStatus("success");
        setMessage(response.data.message);
        setEmail(response.data.email);
        toast.success("Email vérifié avec succès !");
      } catch (error) {
        setStatus("error");
        setMessage(error.response?.data?.detail || "Une erreur est survenue lors de la vérification");
        toast.error(error.response?.data?.detail || "Erreur de vérification");
      }
    };

    verifyEmail();
  }, [token]);

  const handleGoToLogin = () => {
    navigate("/login");
  };

  // No token - show info page
  if (status === "no-token") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 p-8 border border-slate-100 text-center">
            <div className="w-16 h-16 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Mail className="w-8 h-8 text-violet-600" />
            </div>
            <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900 mb-3">
              Vérification d'email
            </h1>
            <p className="text-slate-500 mb-6">
              Cliquez sur le lien dans l'email que nous vous avons envoyé pour vérifier votre adresse email.
            </p>
            <Link to="/login">
              <Button className="w-full h-12 rounded-full bg-gradient-to-r from-violet-600 to-pink-500 hover:opacity-90">
                Aller à la connexion
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (status === "loading") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 p-8 border border-slate-100 text-center">
            <Loader2 className="w-12 h-12 text-violet-600 animate-spin mx-auto mb-6" />
            <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900 mb-3">
              Vérification en cours...
            </h1>
            <p className="text-slate-500">
              Veuillez patienter pendant que nous vérifions votre adresse email.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (status === "success") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 p-8 border border-slate-100 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900 mb-3">
              Email vérifié !
            </h1>
            <p className="text-slate-500 mb-2">
              {message}
            </p>
            {email && (
              <p className="text-sm text-slate-400 mb-6">
                {email}
              </p>
            )}
            <Button 
              onClick={handleGoToLogin}
              className="w-full h-12 rounded-full bg-gradient-to-r from-violet-600 to-pink-500 hover:opacity-90"
              data-testid="verify-email-login-button"
            >
              Se connecter
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 p-8 border border-slate-100 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-10 h-10 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900 mb-3">
            Erreur de vérification
          </h1>
          <p className="text-slate-500 mb-6">
            {message}
          </p>
          <div className="space-y-3">
            <Link to="/login">
              <Button className="w-full h-12 rounded-full bg-gradient-to-r from-violet-600 to-pink-500 hover:opacity-90">
                Aller à la connexion
              </Button>
            </Link>
            <p className="text-sm text-slate-400">
              Besoin d'un nouveau lien ?{" "}
              <Link to="/login" className="text-violet-600 hover:underline">
                Connectez-vous
              </Link>{" "}
              pour en recevoir un nouveau.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
